import '../../../../Components/Form/form.css'

import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'

import {
  DateField,
  FieldSkeleton,
  NumberField,
  SelectField,
  TextField
} from '../../../../Components/Form'
import { cap } from '../../../../State'
import { translations } from '../../../../translations'
import { writeKey } from '../Functions'
import { SectionShell } from '../SectionShell'
import { useGigiFormValidation } from '../ValidationContext'
import { buildCampi1a10Schema, buildDefaultValues } from './Campi1a10.schema'

const language = cap.language.state || 'IT'
const lang = language.toLowerCase()
const t = translations[language] || translations.IT

const getTypeKeys = (item) => {
  if (item?.listSelectValue?.length > 0) {
    const options = []
    item?.listSelectValue.forEach(
      (item) =>
        item.value &&
        !options.some((el) => el.value === item?.value && el.label === item?.label) &&
        options.push(item)
    )
    return { type: 'dropdown', options } // return unique options
  }
  if (item?.type === 'date') return { type: 'date' }
  if (item?.type === 'numeric')
    return { type: 'numeric', label: { it: item?.mainLabel + ' (' + t.gfNumerico + ')' } }
  return {}
}

export const getCampi1a10 = (state) => {
  return state?.transferInfo?.infoPax?.listOfPax?.[0]?.campi1a10?.map((item) => ({
    label: { it: item?.mainLabel },
    location: 'transferInfo.infoPax.listOfPax.0.campi1a10Found.' + item?.key,
    id: item?.key,
    ...getTypeKeys(item),
    notRequired: !state?.environmentInfo?.enableCampi1a10,
    limitLength:
      item?.key === 'campo1' || item?.key === 'campo2' || item?.key === 'campo3' ? 15 : 35 // i campi 1, 2, 3 hanno una lunghezza massima di 15 caratteri, gli altri di 35
  }))
}

const SECTION_ID = 'campi1a10'

const renderField = ({ item, controller, disabled }) => {
  const { field, fieldState } = controller
  const commonProps = {
    id: item.id,
    label: item.label?.[lang] || item.label?.it || item.id,
    value: field.value ?? '',
    onChange: field.onChange,
    onBlur: field.onBlur,
    required: !item.notRequired,
    disabled,
    error: fieldState.error?.message,
    maxLength: item.limitLength
  }
  switch (item.type) {
    case 'dropdown':
      return <SelectField {...commonProps} options={item.options || []} />
    case 'date':
      return <DateField {...commonProps} />
    case 'numeric':
      return <NumberField {...commonProps} />
    default:
      return <TextField {...commonProps} />
  }
}

const SectionInner = ({ items, state, setState, readOnly, agenticLocked }) => {
  const { registerSection, reportErrors, sectionHasErrors } = useGigiFormValidation()

  const schema = useMemo(() => buildCampi1a10Schema(items), [items])
  const defaultValues = useMemo(() => buildDefaultValues(items, state), [items])

  const {
    control,
    trigger,
    setFocus,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const stateRef = useRef(state)
  stateRef.current = state

  useEffect(() => {
    const lop = stateRef.current?.transferInfo?.infoPax?.listOfPax?.[0]
    if (lop && !lop.campi1a10Found) {
      lop.campi1a10Found = {}
      setState({ ...stateRef.current })
    }
  }, [setState])

  const persistField = (id, value) => {
    const loc = `transferInfo.infoPax.listOfPax.0.campi1a10Found.${id}`
    const updated = writeKey(stateRef.current, loc, undefined, value ?? '')
    if (Object.keys(updated).length) setState(updated)
  }

  const errorCount = Object.keys(errors).length
  const errorsRef = useRef(errors)
  errorsRef.current = errors
  const errorCountRef = useRef(errorCount)
  errorCountRef.current = errorCount

  useEffect(() => {
    reportErrors(SECTION_ID, errorCount)
  }, [errorCount, reportErrors])

  useEffect(() => {
    return registerSection(SECTION_ID, {
      isValid: async () => trigger(),
      focusFirstError: () => {
        const firstKey = Object.keys(errorsRef.current)[0]
        if (!firstKey) return false
        const node = document.querySelector(`[data-field="${firstKey}"]`)
        if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' })
        try {
          setFocus(firstKey)
        } catch {
          /* no-op */
        }
        return true
      },
      hasErrors: () => errorCountRef.current > 0
    })
  }, [registerSection, trigger, setFocus])

  return (
    <SectionShell
      id={SECTION_ID}
      icon="sliders"
      title={t.ggCampiPersonalizzati}
      storageKey="gigiForm.section.campi1a10"
      hasErrors={sectionHasErrors(SECTION_ID)}
      contentId="campi1a10-grid"
    >
      <div className="ff-grid">
        {items.map((item) => (
          <div className="ff-grid__item" key={item.id}>
            <Controller
              name={item.id}
              control={control}
              render={(controller) =>
                renderField({
                  item,
                  controller: {
                    field: {
                      ...controller.field,
                      onChange: (value) => {
                        controller.field.onChange(value)
                        persistField(item.id, value)
                      }
                    },
                    fieldState: controller.fieldState
                  },
                  // Campi1a10 è fuori perimetro per il percorso agentico
                  // (requestFromForm non lo legge, decisione già presa — vedi
                  // docs/nora/decisioni.md e 10-divario-legacy.md §2.B): qui i campi
                  // vanno bloccati a prescindere dal `readOnly` generale del form.
                  disabled: readOnly || agenticLocked
                })
              }
            />
          </div>
        ))}
      </div>
    </SectionShell>
  )
}

export const Campi1a10Section = ({ state: [state, setState], readOnly, agenticLocked = false }) => {
  const items = useMemo(() => getCampi1a10(state) ?? [], [state])
  const lastStatus = state?.environmentInfo?.lastStatus

  if (lastStatus === 'RUNNING' && !items.length) {
    return (
      <SectionShell
        id={SECTION_ID}
        icon="sliders"
        title={t.ggCampiPersonalizzati}
        storageKey="gigiForm.section.campi1a10"
        contentId="campi1a10-grid"
      >
        <div className="ff-grid">
          <FieldSkeleton count={6} />
        </div>
      </SectionShell>
    )
  }

  if (!items.length) return null

  return (
    <SectionInner
      key={items.map((i) => `${i.id}:${i.type ?? 'text'}`).join('|')}
      items={items}
      state={state}
      setState={setState}
      readOnly={readOnly}
      agenticLocked={agenticLocked}
    />
  )
}
